import { html, nothing } from "lit";
import type { SessionsListResult } from "../types.ts";
import {
  parseSessionKeyPath,
  sessionKeyFromPath,
  type SessionKeyPath,
} from "../chat/session-key-path.ts";
import { renderSessionPathNavigator } from "./session-path-navigator.ts";

export type LlmStatsScopeBarProps = {
  viewAll: boolean;
  scopeLinked: boolean;
  scopePath: SessionKeyPath;
  fleetSessionKey: string;
  openTabs: string[];
  sessions: SessionsListResult | null;
  connected: boolean;
  scopedSessionCount: number;
  totalSessionCount: number;
  onToggleViewAll: (viewAll: boolean) => void;
  onToggleLinked: (linked: boolean) => void;
  onScopePathChange: (path: SessionKeyPath) => void;
};

export function renderLlmStatsScopeBar(props: LlmStatsScopeBarProps) {
  const activeSessionKey = sessionKeyFromPath(props.scopePath) ?? props.fleetSessionKey;

  return html`
    <style>
      .llm-stats-scope-bar {
        margin-bottom: 16px;
      }
    </style>
    <div
      class="llm-stats-scope-bar sessions-scope-bar fleet-session-bar logs-scope-bar"
      role="region"
      aria-label="LLM stats scope"
    >
      <div class="row" style="justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap;">
        <div class="chip-row" style="margin: 0;">
          <label class="chip log-chip ${props.viewAll ? "active" : ""}">
            <input
              type="radio"
              name="llm-stats-view-mode"
              .checked=${props.viewAll}
              @change=${() => props.onToggleViewAll(true)}
            />
            <span>All sessions</span>
          </label>
          <label class="chip log-chip ${!props.viewAll ? "active" : ""}">
            <input
              type="radio"
              name="llm-stats-view-mode"
              .checked=${!props.viewAll}
              @change=${() => props.onToggleViewAll(false)}
            />
            <span>Session navigator</span>
          </label>
        </div>
        ${
          !props.viewAll
            ? html`
                <button
                  type="button"
                  class="btn btn--sm"
                  @click=${() => props.onToggleLinked(!props.scopeLinked)}
                >
                  ${props.scopeLinked ? "Unlink from Fleet" : "Re-link to Fleet"}
                </button>
              `
            : nothing
        }
      </div>
      ${
        props.viewAll
          ? html`
              <p style="margin: 8px 0 0; font-size: 12px; color: var(--muted);">
                Showing all ${props.totalSessionCount} sessions in the date range.
              </p>
            `
          : html`
              <p style="margin: 8px 0 0; font-size: 12px; color: var(--muted);">
                ${
                  props.scopeLinked ? "Following Fleet session" : "Custom scope (agent → kind → id)"
                }
                — ${props.scopedSessionCount} of ${props.totalSessionCount} sessions match
              </p>
            `
      }
      ${
        props.viewAll
          ? nothing
          : html`
              <div style="margin-top: 10px;">
                ${renderSessionPathNavigator({
                  activeSessionKey,
                  activePath: props.scopePath,
                  openTabs: props.openTabs,
                  sessions: props.sessions,
                  onSelectSession: (key) => {
                    if (props.scopeLinked) {
                      props.onToggleLinked(false);
                    }
                    props.onScopePathChange(parseSessionKeyPath(key));
                  },
                  showActiveFooter: false,
                  ariaLabel: "LLM stats scope",
                  className: "fleet-session-path sessions-scope-path",
                })}
              </div>
            `
      }
      ${
        !props.connected
          ? html`
              <div style="margin-top: 8px; font-size: 12px; color: var(--muted)">
                Connect to the gateway to discover session keys.
              </div>
            `
          : nothing
      }
    </div>
  `;
}
